import {
  toDeliveryAddressParams,
  useCheckoutStatus,
  useGroupSpecialSelection,
  useLocalDeliveryAddress,
  updateLocalCartUuid,
  useLocalCartUuid,
} from '@iheartjane/cart/utils';
import { ApiError } from '@iheartjane/data-access';
import type { Cart, UpdateCartProps } from '@iheartjane/data-access';
import { cartMutationKeys, useCart } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { Address } from '@iheartjane/types';
import { keepPreviousData, useIsMutating } from '@tanstack/react-query';
import type { MutationFilters } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { selectCart } from '@jane/monolith/customer/redux/cart';
import { selectEmbeddedApp } from '@jane/monolith/customer/redux/embeddedApp';
import { selectPosCart } from '@jane/monolith/customer/redux/posCart';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { getJwt } from '@jane/shared/auth';
import { useEcommApp } from '@jane/shared/providers';

import { mapV1CartToV3 } from './mapV1CartToV3';
import type { V1CartInput } from './mapV1CartToV3';

type MutationPredicate = NonNullable<MutationFilters['predicate']>;

// An update in flight that sends `field`; `null` counts, since it clears.
const updateSending =
  (field: keyof UpdateCartProps): MutationPredicate =>
  (mutation) =>
    (mutation.state.variables as UpdateCartProps | undefined)?.[field] !==
    undefined;

const sendsReservationMode = updateSending('reservationMode');
const sendsGroupSpecialId = updateSending('userGroupSpecialId');
// Mode changes re-send the pick too; only a pick on its own is a group write.
const sendsGroupSpecial: MutationPredicate = (mutation) =>
  sendsGroupSpecialId(mutation) && !sendsReservationMode(mutation);

/**
 * The cart in the v3 `Cart` shape: from `GET /v3/carts/:uuid` flag-on, mapped
 * from the v1 slice flag-off.
 *
 * Don't enable the flag until every write has moved to v3: v1 writes only
 * update the slice, and v3 writes only update the query cache.
 * TODO(carts-v3): remove the flag-off path once v3 is the only source.
 */
export interface CartFacadeState {
  cart: Cart | null;
  /** Sum of product counts, not the number of lines. */
  cartSize: number;
  /** Not returned by the API: in the slice flag-off, in storage flag-on. */
  deliveryAddress: Address | null;
  /** The shopper's pick; `cart.group_special_id` is null when no line qualifies. */
  groupSpecialSelection: number | null;
  /** The load has settled, even with no cart. */
  hasLoaded: boolean;
  hasSpecial: boolean;
  isApplyingGroupSpecial: boolean;
  /**
   * Only `applyLoyalty`, not `isMutating`: it guards the reward buttons against
   * double-applying, and shouldn't block them during other cart writes.
   */
  isApplyingLoyalty: boolean;
  /**
   * A mode-change update is in flight; drives the totals shimmer. Flag-on,
   * only updates that send `reservationMode` count, not group-special ones.
   */
  isChangingReservationMode: boolean;
  isFetching: boolean;
  isLoading: boolean;
  /**
   * A cart write is in flight. Not every write is visible:
   * - Flag-off: `updateProduct`, `removeProduct`, `removeLoyalty` and
   *   `updateDeliveryAddress` set no slice flag.
   * - Flag-on: `deleteUnavailableProducts` skips the mutation cache. The
   *   checkout status store covers the reCAPTCHA and MFA steps before the
   *   checkout mutation starts.
   */
  isMutating: boolean;
  orderTotal: number;
}

interface CartStateOptions {
  /** Flag-on only: reads the cart priced for this method under its own key. */
  paymentMethod?: string;
}

export const useCartState = ({
  paymentMethod,
}: CartStateOptions = {}): CartFacadeState => {
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { appMode } = useEcommApp();
  // Only used to adopt a legacy cart uuid
  const { partnerId: storeId } = useCustomerSelector(selectEmbeddedApp);
  // Skipped flag-off, where the slice is the source
  const localCartUuid = useLocalCartUuid(appMode, storeId, !!useV3Cart);

  const localDeliveryAddress = useLocalDeliveryAddress(!!useV3Cart);

  const {
    analyzingSpecials,
    cart: v1Cart,
    deliveryAddress: v1DeliveryAddress,
    isLoading,
    hasLoaded,
    isAddingItemToCart,
    isApplyingCrmRedemption,
    isChangingReservationModes,
    isDeletingUnavailableProducts,
    isFixingUnavailableCheckout,
  } = useCustomerSelector(selectCart);
  const { isCheckingOut } = useCheckoutStatus();

  // Without the address the delivery fee comes back as 0.
  const deliveryAddress = useV3Cart
    ? toDeliveryAddressParams(localDeliveryAddress)
    : undefined;

  const v3Cart = useCart(
    {
      appMode,
      deliveryAddress,
      jwt: useV3Cart ? getJwt() : null,
      paymentMethod: useV3Cart ? paymentMethod : undefined,
      uuid: localCartUuid ?? '',
    },
    // Keep the old cart on screen while an address change refetches. Set here,
    // not in `cart.queries.ts`, because kiosk shares that hook.
    { enabled: !!useV3Cart, placeholderData: keepPreviousData }
  );

  // A 404 means the cart was destroyed (last product, checkout, expiry). Drop
  // the uuid so consumers see an empty bag; `removeQueries` would refetch.
  const cartGone =
    !!useV3Cart &&
    !!localCartUuid &&
    v3Cart.error instanceof ApiError &&
    v3Cart.error.response.status === 404;

  useEffect(() => {
    if (cartGone) updateLocalCartUuid(appMode, storeId, null);
  }, [appMode, cartGone, storeId]);

  // Flag-off only: v1 keeps POS totals on their own slice; v3 includes them.
  const posCartTotals =
    useCustomerSelector(selectPosCart).posCart.totals ?? null;

  // `isPending` is per hook instance, so read in-flight writes from the
  // mutation cache. Prefix match: `all` counts every cart mutation. 0 flag-off,
  // as long as `useCartActions` is the only cart-mutation caller here.
  const cartMutationCount = useIsMutating({
    mutationKey: cartMutationKeys.all,
  });
  const addLoyaltyMutationCount = useIsMutating({
    mutationKey: cartMutationKeys.addLoyaltyRedemption,
  });
  const groupSpecialMutationCount = useIsMutating({
    mutationKey: cartMutationKeys.update,
    predicate: sendsGroupSpecial,
  });
  const reservationModeMutationCount = useIsMutating({
    mutationKey: cartMutationKeys.update,
    predicate: sendsReservationMode,
  });

  // No cart = empty uuid. The cast drops `DeepReadonly` (the mapper doesn't
  // mutate) and matches the slice's runtime shape, which `V1CartInput` models.
  const mappedV1Cart = useMemo(
    () =>
      !useV3Cart && v1Cart.uuid
        ? mapV1CartToV3(v1Cart as unknown as V1CartInput, posCartTotals)
        : null,
    [useV3Cart, v1Cart, posCartTotals]
  );

  const cart = useV3Cart ? (v3Cart.data ?? null) : mappedV1Cart;
  const groupSpecialSelection = useGroupSpecialSelection(
    useV3Cart ? localCartUuid : v1Cart.uuid
  );

  const cartSize = cart
    ? cart.products.reduce((total, product) => total + product.count, 0)
    : 0;

  const hasSpecial = Boolean(
    cart &&
    (cart.cart_discount_special_id != null ||
      cart.group_special_id != null ||
      cart.brand_discounts.length > 0 ||
      cart.products.some((product) => product.special_id != null))
  );

  return {
    cart,
    // With no uuid the query never runs, so "settled" is immediate.
    hasLoaded: useV3Cart ? !localCartUuid || v3Cart.isFetched : hasLoaded,
    isLoading: useV3Cart ? v3Cart.isLoading : isLoading && !hasLoaded,
    isFetching: useV3Cart ? v3Cart.isFetching : isLoading,
    // TODO(carts-v3): drop `isFixingUnavailableCheckout` flag-on once that fix moves to v3.
    isMutating: useV3Cart
      ? cartMutationCount > 0 || isCheckingOut || isFixingUnavailableCheckout
      : isAddingItemToCart ||
        isApplyingCrmRedemption ||
        isChangingReservationModes ||
        isCheckingOut ||
        isDeletingUnavailableProducts ||
        isFixingUnavailableCheckout,
    isApplyingLoyalty: useV3Cart
      ? addLoyaltyMutationCount > 0
      : isApplyingCrmRedemption,
    isApplyingGroupSpecial: useV3Cart
      ? groupSpecialMutationCount > 0
      : analyzingSpecials,
    isChangingReservationMode: useV3Cart
      ? reservationModeMutationCount > 0
      : isChangingReservationModes,
    cartSize,
    deliveryAddress: useV3Cart
      ? localDeliveryAddress
      : (v1DeliveryAddress ?? null),
    groupSpecialSelection,
    hasSpecial,
    orderTotal: cart?.order_total ?? 0,
  };
};
